
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { StudentContextProps, StudentData } from './types';
import { useStudentOperations } from './hooks/useStudentOperations';
import { useNotificationsCheck } from './hooks/useNotificationsCheck';
import { ProgramCategory } from '@/services/db/types';
import { useProgramLists } from '@/hooks/useProgramLists';
import { autoBackupService } from '@/services/storage/autoBackupService';

// Create context with default values
const StudentContext = createContext<StudentContextProps>({
  students: [],
  loading: true,
  initialized: false,
  programCategories: ['DIT Programs', 'Language', 'Web Development', 'Programming Languages'],
  programsByCategory: {
    'DIT Programs': ['Basic Computer', 'Advanced Computer'],
    'Language': ['English Basic', 'English Advanced', 'Turkish', 'Russian', 'Arabic'],
    'Web Development': ['HTML/CSS', 'JavaScript', 'React'],
    'Programming Languages': ['Python', 'Java', 'C++'],
    'Networking': ['Basic Networking', 'Advanced Networking'],
    'Special Packages': ['Full Stack Developer', 'Data Science'],
    'English File': ['Beginner', 'Elementary', 'Pre-Intermediate'],
    'CEL': ['Level 1', 'Level 2', 'Level 3'],
    'DEL': ['Basic', 'Intermediate', 'Advanced']
  },
  getStudent: () => undefined,
  saveStudent: async () => {},
  saveStudentList: async () => {},
  refreshStudents: async () => [],
  checkNotifications: async () => [],
  getCompletingStudents: async () => [],
  getUpcomingPayments: async () => [],
  addStudent: async () => 0,
  updateStudent: async () => {},
  deleteStudent: async () => {},
  searchStudents: async () => [],
  loadBackup: () => {},
});

// Provider component
export const StudentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    students,
    loading,
    initialized,
    getStudent,
    addStudent,
    updateStudent,
    deleteStudent,
    searchStudents,
    refreshStudents,
    initializeStudentData,
    saveStudent,
    saveStudentList,
    loadBackup,
  } = useStudentOperations();
  
  // Get notification functions with safe defaults
  const notificationHooks = useNotificationsCheck(students || []);
  
  // Create stable notification functions
  const checkNotifications = useCallback(async () => {
    if (notificationHooks?.checkNotifications) {
      return await notificationHooks.checkNotifications();
    }
    return [];
  }, [notificationHooks?.checkNotifications]);

  const getCompletingStudents = useCallback(async () => {
    if (notificationHooks?.getCompletingStudents) {
      return await notificationHooks.getCompletingStudents();
    }
    return [];
  }, [notificationHooks?.getCompletingStudents]);

  const getUpcomingPayments = useCallback(async () => {
    if (notificationHooks?.getUpcomingPayments) {
      return await notificationHooks.getUpcomingPayments();
    }
    return [];
  }, [notificationHooks?.getUpcomingPayments]);

  const { programCategories, programsByCategory } = useProgramLists();

  // Start auto-backup service on mount
  useEffect(() => {
    console.log('🛡️ Initializing auto-backup service for data persistence...');
    autoBackupService.start();
    
    return () => {
      autoBackupService.stop();
    };
  }, []);

  // Initialize only once when component mounts
  useEffect(() => {
    let mounted = true;
    
    const initialize = async () => {
      if (!initialized && mounted && initializeStudentData) {
        console.log('🚀 StudentProvider: Initializing with enhanced storage...');
        try {
          await initializeStudentData();
        } catch (error) {
          console.error('❌ StudentProvider initialization failed:', error);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [initialized]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    students: students || [],
    loading,
    initialized,
    programCategories: programCategories || [],
    programsByCategory: programsByCategory || {
      'DIT Programs': ['Basic Computer', 'Advanced Computer'],
      'Language': ['English Basic', 'English Advanced', 'Turkish', 'Russian', 'Arabic'],
      'Web Development': ['HTML/CSS', 'JavaScript', 'React'],
      'Programming Languages': ['Python', 'Java', 'C++'],
      'Networking': ['Basic Networking', 'Advanced Networking'],
      'Special Packages': ['Full Stack Developer', 'Data Science'],
      'English File': ['Beginner', 'Elementary', 'Pre-Intermediate'],
      'CEL': ['Level 1', 'Level 2', 'Level 3'],
      'DEL': ['Basic', 'Intermediate', 'Advanced']
    },
    getStudent: getStudent || (() => undefined),
    saveStudent: saveStudent || (async () => {}),
    saveStudentList: saveStudentList || (async () => {}),
    refreshStudents: refreshStudents || (async () => []),
    checkNotifications,
    getCompletingStudents,
    getUpcomingPayments,
    addStudent: addStudent || (async () => 0),
    updateStudent: updateStudent || (async () => {}),
    deleteStudent: deleteStudent || (async () => {}),
    searchStudents: searchStudents || (async () => []),
    loadBackup: loadBackup || (() => {}),
  }), [
    students,
    loading,
    initialized,
    programCategories,
    programsByCategory,
    checkNotifications,
    getCompletingStudents,
    getUpcomingPayments
  ]);

  // Global context setup - only update when students array changes
  useEffect(() => {
    if (typeof window !== 'undefined' && students && Array.isArray(students) && students.length > 0) {
      window.__NALC__CONTEXT = {
        ...window.__NALC__CONTEXT,
        studentContext: {
          students,
          loadBackup: loadBackup || (() => {}),
          getUpcomingPayments: getUpcomingPayments || (async () => []),
          saveStudent: saveStudent || (async () => {}),
          saveStudentList: saveStudentList || (async () => {})
        }
      };
    }
    
    return () => {
      if (typeof window !== 'undefined' && window.__NALC__CONTEXT) {
        delete window.__NALC__CONTEXT.studentContext;
      }
    };
  }, [students]);

  return (
    <StudentContext.Provider value={contextValue}>
      {children}
    </StudentContext.Provider>
  );
};

// Single student hook - returns a specific student by ID
export const useStudent = (id?: number) => {
  const context = useContext(StudentContext);
  
  if (context === undefined) {
    throw new Error('useStudent must be used within a StudentProvider');
  }
  
  if (id === undefined) {
    return { student: undefined, loading: context.loading };
  }
  
  return {
    student: context.getStudent(id),
    loading: context.loading
  };
};

// All students hook - returns all students and operations
export const useStudents = () => {
  const context = useContext(StudentContext);
  
  if (context === undefined) {
    throw new Error('useStudents must be used within a StudentProvider');
  }
  
  return context;
};

// Add global type declaration
declare global {
  interface Window {
    __NALC__CONTEXT?: {
      studentContext?: {
        students: StudentData[];
        loadBackup: (data: StudentData[]) => void;
        getUpcomingPayments?: (days: number) => Promise<any[]>;
        saveStudent?: (student: StudentData) => Promise<void>;
        saveStudentList?: (students: StudentData[]) => Promise<void>;
      };
    };
  }
}
